import {
  ChevronRight,
  Ellipsis,
  Folder,
  FolderOpen,
  FolderPlus,
  Pencil,
  Trash2,
} from 'lucide-react';
import { useState, type DragEvent } from 'react';

import type { FolderBody } from '@/shared/api/generated/model';
import { Button } from '@/shared/ui/Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/ui/DropdownMenu';

import styles from './MediaLibrary.module.scss';

/** Тип перетаскиваемого из сетки: файлы медиатеки, а не файлы с компьютера. */
export const MEDIA_DRAG = 'application/x-faceta-media';

export interface FolderTreeProps {
  folders: FolderBody[];
  /** Открытая папка; null — корень; undefined — открыт не корень и не папка (быстрый вид). */
  current: string | null | undefined;
  canWrite: boolean;
  onOpen: (folder: string | null) => void;
  /** Файлы перетащили на папку (null — в корень). */
  onDrop: (folder: string | null, ids: string[]) => void;
  onCreate: (parent: string | null) => void;
  onRename: (folder: FolderBody) => void;
  onDelete: (folder: FolderBody) => void;
}

/**
 * Дерево папок. Файлы из сетки перетаскиваются на папку — как на
 * компьютере; ветки раскрываются стрелкой, а открытая папка раскрывает путь
 * к себе сама.
 */
export function FolderTree(props: FolderTreeProps) {
  const { folders, current } = props;
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  // Путь к открытой папке раскрыт всегда: иначе она пропадала бы из виду.
  const onPath = new Set<string>();
  let cursor = folders.find((item) => item.id === current);

  while (cursor?.parent_id !== undefined) {
    onPath.add(cursor.parent_id);
    cursor = folders.find((item) => item.id === cursor?.parent_id);
  }

  const children = (parent: string | undefined) =>
    folders.filter((item) => item.parent_id === parent);

  const branch = (parent: string | undefined, depth: number) =>
    children(parent).map((folder) => {
      const kids = children(folder.id);
      const open = expanded[folder.id] === true || onPath.has(folder.id);

      return (
        <li key={folder.id}>
          <FolderRow
            {...props}
            folder={folder}
            depth={depth}
            open={open}
            hasChildren={kids.length > 0}
            onToggle={() => {
              setExpanded((prev) => ({ ...prev, [folder.id]: !open }));
            }}
          />
          {open && kids.length > 0 && (
            <ul className={styles['tree']}>{branch(folder.id, depth + 1)}</ul>
          )}
        </li>
      );
    });

  return (
    <div className={styles['folders']}>
      <DropTarget
        active={current === null}
        onOpen={() => {
          props.onOpen(null);
        }}
        onDrop={(ids) => {
          props.onDrop(null, ids);
        }}
        label="Все папки"
        icon={current === null ? <FolderOpen size={15} /> : <Folder size={15} />}
        depth={0}
        actions={
          props.canWrite ? (
            <Button
              iconOnly
              size="sm"
              variant="ghost"
              tone="neutral"
              aria-label="Новая папка в корне"
              onClick={() => {
                props.onCreate(null);
              }}
            >
              <FolderPlus size={14} />
            </Button>
          ) : null
        }
      />
      <ul className={styles['tree']}>{branch(undefined, 1)}</ul>
    </div>
  );
}

function FolderRow({
  folder,
  depth,
  open,
  hasChildren,
  onToggle,
  current,
  canWrite,
  onOpen,
  onDrop,
  onCreate,
  onRename,
  onDelete,
}: FolderTreeProps & {
  folder: FolderBody;
  depth: number;
  open: boolean;
  hasChildren: boolean;
  onToggle: () => void;
}) {
  return (
    <DropTarget
      active={current === folder.id}
      depth={depth}
      label={folder.name}
      count={folder.files}
      icon={
        <span className={styles['tree__lead']}>
          {hasChildren ? (
            <button
              type="button"
              className={`${styles['tree__toggle'] ?? ''} ${open ? (styles['tree__toggle--open'] ?? '') : ''}`}
              aria-label={open ? `Свернуть «${folder.name}»` : `Раскрыть «${folder.name}»`}
              onClick={(event) => {
                event.stopPropagation();
                onToggle();
              }}
            >
              <ChevronRight size={12} />
            </button>
          ) : (
            <span className={styles['tree__toggle']} />
          )}
          {current === folder.id ? <FolderOpen size={15} /> : <Folder size={15} />}
        </span>
      }
      onOpen={() => {
        onOpen(folder.id);
      }}
      onDrop={(ids) => {
        onDrop(folder.id, ids);
      }}
      actions={
        canWrite ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                iconOnly
                size="sm"
                variant="ghost"
                tone="neutral"
                aria-label={`Папка «${folder.name}»`}
              >
                <Ellipsis size={14} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuItem
                icon={<FolderPlus size={15} />}
                onSelect={() => {
                  onCreate(folder.id);
                }}
              >
                Папка внутри
              </DropdownMenuItem>
              <DropdownMenuItem
                icon={<Pencil size={15} />}
                onSelect={() => {
                  onRename(folder);
                }}
              >
                Переименовать
              </DropdownMenuItem>
              <DropdownMenuItem
                icon={<Trash2 size={15} />}
                tone="danger"
                onSelect={() => {
                  onDelete(folder);
                }}
              >
                В корзину
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null
      }
    />
  );
}

/** Строка дерева, на которую можно бросить файлы. */
function DropTarget({
  active,
  depth,
  label,
  count,
  icon,
  actions,
  onOpen,
  onDrop,
}: {
  active: boolean;
  depth: number;
  label: string;
  count?: number;
  icon: React.ReactNode;
  actions: React.ReactNode;
  onOpen: () => void;
  onDrop: (ids: string[]) => void;
}) {
  const [over, setOver] = useState(false);

  const accepts = (event: DragEvent) => event.dataTransfer.types.includes(MEDIA_DRAG);

  return (
    <div
      className={`${styles['tree__row'] ?? ''} ${active ? (styles['tree__row--active'] ?? '') : ''} ${
        over ? (styles['tree__row--over'] ?? '') : ''
      }`}
      style={{ paddingLeft: `calc(var(--space-2) + ${String(depth * 14)}px)` }}
      onDragOver={(event) => {
        if (accepts(event)) {
          event.preventDefault();
          setOver(true);
        }
      }}
      onDragLeave={() => {
        setOver(false);
      }}
      onDrop={(event) => {
        setOver(false);

        if (!accepts(event)) {
          return;
        }

        event.preventDefault();

        const raw = event.dataTransfer.getData(MEDIA_DRAG);

        try {
          const ids = JSON.parse(raw) as unknown;

          if (Array.isArray(ids)) {
            onDrop(ids.filter((id): id is string => typeof id === 'string'));
          }
        } catch {
          // Чужие данные — не наши файлы.
        }
      }}
    >
      <button
        type="button"
        className={styles['tree__open']}
        onClick={onOpen}
        aria-current={active ? 'true' : undefined}
      >
        {icon}
        <span className={styles['tree__label']}>{label}</span>
        {count !== undefined && count > 0 && <span className={styles['tree__count']}>{count}</span>}
      </button>
      {actions}
    </div>
  );
}
